import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import './Items.css';

function Items() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const lastItemElementRef = useRef();
  const scrollContainerRef = useRef();
  const isFirstRender = useRef(true);

  const loadItems = useCallback(async (pageNum, search = '', shouldReset = false) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/items?page=${pageNum}&limit=20&search=${search}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch items');
      }
      const responseData = await response.json();
      const { data } = responseData;
      
      setItems(prev => shouldReset ? data.items : [...prev, ...data.items]);
      setHasMore(data.pagination.hasNextPage);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadItems(1, '', true);
    const currentObserverRef = observer.current;
    return () => {
      if (currentObserverRef) {
        currentObserverRef.disconnect();
      }
    };
  }, [loadItems]);

  // Handle search with debounce — always refetch, including when search is cleared.
  // Skip the first render since the initial load useEffect already covers it.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setPage(1);
      loadItems(1, searchTerm, true);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadItems]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '20px',
      threshold: 0.1,
    };

    const handleObserver = (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadItems(nextPage, searchTerm, false);
      }
    };

    const currentObserver = new IntersectionObserver(handleObserver, options);
    const lastItemElement = lastItemElementRef.current;

    if (lastItemElement) {
      currentObserver.observe(lastItemElement);
    }

    return () => {
      if (lastItemElement) {
        currentObserver.unobserve(lastItemElement);
      }
    };
  }, [loading, hasMore, page, searchTerm, loadItems]);

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="items-page-container">
      <h2>Pokémon Items</h2>
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search items by name or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div 
        ref={scrollContainerRef}
        className="items-content-container"
      >
        <div className="items-grid">
          {items.map((item, index) => (
            <Link 
              ref={index === items.length - 1 ? lastItemElementRef : null}
              to={`/items/${item.name}`} 
              key={item.name} 
              className="item-card"
            >
              <div className="item-header">
                <div className="title-with-sprite">
                  <img src={item.sprite} alt={item.name} className="item-sprite" />
                  <h3>{item.name}</h3>
                </div>
                <span className="category-badge">{item.category}</span>
              </div>
              <p className="item-effect">{item.shortEffect}</p>
            </Link>
          ))}
        </div>
        {loading && <div className="loading">Loading items...</div>}
      </div>
    </div>
  );
}

export default Items; 